import { NavLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { allowsNavItem, NAV_ITEMS, SECTION_ORDER, type NavItem } from '@/config/nav';
import { useAuth } from '@/hooks/useAuth';
import { isRootLevel } from '@/stores/auth-store';
import { devToolsEnabled } from '@/env';
import { useBoBrand } from '@/hooks/useBoBrand';
import { BrandLogo } from '@/components/layout/BrandLogo';
import { useTicketSummary } from '@/hooks/useTicketSummary';

export function Sidebar() {
  const { t } = useTranslation();
  const { hasPermission, user } = useAuth();
  const { name } = useBoBrand();

  // A dev instrument is offered only in a dev server or a build that opted in
  // (the dev cluster), and only to the rung that already bypasses every
  // server-side check. Both halves are repeated by the page itself — hiding a
  // link is not a guard.
  const devToolsAllowed = devToolsEnabled() && isRootLevel(user);
  const visibleItems = NAV_ITEMS.filter(
    (item) => allowsNavItem(item, hasPermission) && (!item.devOnly || devToolsAllowed),
  );
  const grouped = SECTION_ORDER.map((section) => ({
    section,
    items: visibleItems.filter((item) => item.section === section),
  })).filter((group) => group.items.length > 0);

  return (
    <aside className="hidden w-64 shrink-0 border-r border-sidebar-border bg-sidebar text-sidebar-foreground md:flex md:flex-col">
      <div className="flex h-16 items-center gap-2 border-b border-sidebar-border px-5">
        <BrandLogo />
        <div className="flex flex-col leading-tight">
          <span className="text-sm font-semibold">{name ?? t('app.name')}</span>
          <span className="text-xs text-muted-foreground">{t('app.tagline')}</span>
        </div>
      </div>
      <nav className="flex-1 overflow-y-auto p-3">
        {grouped.map((group, idx) => (
          <div key={group.section} className={cn(idx > 0 && 'mt-4')}>
            <div className="px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {group.section}
            </div>
            <ul className="flex flex-col gap-0.5">
              {group.items.map((item) => (
                <SidebarItem key={item.path} item={item} />
              ))}
            </ul>
          </div>
        ))}
      </nav>
    </aside>
  );
}

/**
 * A menu item's live count.
 *
 * The hook is called unconditionally — it is a hook, and `useTicketSummary`
 * already declines to fetch for a role without `ticket:read`, so a menu that
 * does not show the tickets item costs nothing. Rendering it as `null` rather
 * than `0` is deliberate: a queue with nothing in it wears no badge at all.
 *
 * The figure is `queued` — the tickets NOBODY HAS PICKED UP. `open` (queued +
 * accepted) counts a ticket a colleague is already working on, which is the one
 * thing a "go and take one" badge must not do: it never falls to zero while
 * anybody is mid-investigation, so it stops meaning "there is work waiting".
 */
function NavBadge({ kind }: { kind: NonNullable<NavItem['badge']> }) {
  const tickets = useTicketSummary();
  if (kind !== 'ticketsQueued') return null;
  const count = tickets.data?.queued ?? 0;
  if (count <= 0) return null;
  return (
    <span
      data-testid="nav-badge-ticketsQueued"
      className="ml-auto flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-primary px-1.5 text-[10px] font-semibold leading-none text-primary-foreground"
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

function SidebarItem({ item }: { item: NavItem }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  // The label keeps the ordinary foreground colour on purpose: `--primary` and
  // `--sidebar-accent` carry the same value in both themes and every brand, so
  // coloured text here would read as a half-selected row — and the sidebar
  // surface is not one of the floored ones, so it can fall under AA unnoticed.
  // The tint, the ring and the solid pill carry the signal instead.
  const marked = item.devOnly === true;
  return (
    <li>
      <NavLink
        to={item.path}
        end={item.path === '/'}
        title={item.descriptionKey ? t(item.descriptionKey) : undefined}
        className={({ isActive }) =>
          cn(
            'flex items-center gap-2 rounded-md px-3 py-2 text-sm transition-colors',
            isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground'
              : 'text-sidebar-foreground hover:bg-sidebar-accent/20',
            marked && !isActive && 'bg-primary/10 ring-1 ring-inset ring-primary/30 hover:bg-primary/20',
          )
        }
      >
        <Icon className="h-4 w-4 shrink-0" />
        <span className="truncate">{t(item.labelKey)}</span>
        {marked && (
          <span
            data-testid="nav-dev-pill"
            className="ml-auto rounded-full bg-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary-foreground"
          >
            dev
          </span>
        )}
        {item.badge && <NavBadge kind={item.badge} />}
      </NavLink>
    </li>
  );
}
